// YourSourcesPanel — the New project box's "Your sources" (#312, pen "Ny
// projekt-ruta"; pen "New project box: many files"): the uploaded sources not
// in Files yet, with search, one click or one drag into Files. What is in
// Files is one summary line at the top ("{n} added to Files", Show), so
// nothing appears twice; a file taken out of Files comes back here.
//
// Pure: the list, what is added and whether the box is full are props; every
// gesture is reported up. Local state is view-ephemeral only (the search
// text, the keyboard row, how many pages show, the row being dragged).
//
// The search is a combobox like SourcePicker's (the shared `listKey` rule):
// the input keeps focus and points at the active row; arrows move and Enter
// adds, so a drag is never the only way (WCAG 2.5.7). No chord is
// registered. Titles are plain text, never markup.

export { kindIcon, YourSourcesPanel };

import { exhaustSwitchCase } from "@bilda/utils";
import { plural } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { type ReactNode, useRef, useState } from "react";
import {
    groupYourSources,
    listKey,
    SOURCE_SEARCH_MAX,
    type SourceKind,
    YOUR_SOURCES_PAGE,
    type YourSource,
    type YourSourcesState,
} from "../../app/yourSources.js";
import { Button } from "../Button/Button.js";
import {
    encodeResourceDrag,
    RESOURCE_DRAG_MIME,
} from "../Composer/PowerComposer/resourceDrag.js";
import { Icon, type IconName } from "../Icon/Icon.js";
import { Input } from "../Input/Input.js";
import { Spinner } from "../Spinner/Spinner.js";
import { Text } from "../Text/Text.js";
import "./YourSourcesPanel.css";

type YourSourcesPanelProps = {
    // Never `ready` with no sources: the box leaves the panel out then.
    readonly state: YourSourcesState;
    // The Name and the description, for "Matches what you wrote".
    readonly written: string;
    // The ids of the sources already in Files: left out of the list.
    readonly added: ReadonlySet<string>;
    // How many lines Files holds (uploads and unreadable files included).
    readonly inFiles: number;
    // Files holds as many as it takes: + rows wait until one is removed.
    readonly full: boolean;
    readonly onAdd: (source: YourSource) => void;
    readonly onRetry: () => void;
    readonly onShowAll: () => void;
    // Show on the summary line: scrolls to the Files list and opens it.
    readonly onShowFiles: () => void;
};

// A kind's icon; an unknown kind gets the generic file icon.
const kindIcon = (kind: SourceKind): IconName => {
    switch (kind) {
        case "document":
            return "document";
        case "presentation":
            return "presentation";
        case "image":
            return "image";
        case "unknown":
            return "unknown-file";
        default:
            return exhaustSwitchCase(kind);
    }
};

const YourSourcesPanel = ({
    state,
    written,
    added,
    inFiles,
    full,
    onAdd,
    onRetry,
    onShowAll,
    onShowFiles,
}: YourSourcesPanelProps): ReactNode => {
    const { t } = useLingui();
    return (
        <section
            className="bilda-your-sources"
            aria-labelledby="your-sources-title"
        >
            <div className="bilda-your-sources__head">
                <h3
                    id="your-sources-title"
                    className="bilda-your-sources__title"
                >
                    {t`Your sources`}
                </h3>
                <Text as="p" size="sm" tone="dim">
                    {t`Click + or drag one into Files.`}
                </Text>
            </div>
            {((): ReactNode => {
                switch (state.type) {
                    case "loading":
                        return (
                            <Text
                                as="p"
                                size="sm"
                                tone="dim"
                                className="bilda-your-sources__line"
                            >
                                <Spinner size="sm" />
                                {t`Fetching your sources…`}
                            </Text>
                        );
                    case "failed":
                        return (
                            <div className="bilda-your-sources__line">
                                <Text as="p" size="sm" tone="dim">
                                    {t`Couldn't load your sources. Try again.`}
                                </Text>
                                <Button variant="secondary" onClick={onRetry}>
                                    {t`Try again`}
                                </Button>
                            </div>
                        );
                    case "ready":
                        return (
                            <SourceList
                                sources={state.sources.filter(
                                    (s) => !added.has(s.id),
                                )}
                                written={written}
                                inFiles={inFiles}
                                full={full}
                                onAdd={onAdd}
                                onShowFiles={onShowFiles}
                            />
                        );
                    default:
                        return exhaustSwitchCase(state);
                }
            })()}
            <button
                type="button"
                className="bilda-your-sources__all"
                onClick={onShowAll}
            >
                {t`Show all in Uploaded sources`}
            </button>
        </section>
    );
};

const LISTBOX_ID = "your-sources-list";
const optionId = (i: number): string => `${LISTBOX_ID}-${i}`;

// The search, the summary line and the sources not in Files yet.
const SourceList = ({
    sources,
    written,
    inFiles,
    full,
    onAdd,
    onShowFiles,
}: {
    readonly sources: readonly YourSource[];
    readonly written: string;
    readonly inFiles: number;
    readonly full: boolean;
    readonly onAdd: (source: YourSource) => void;
    readonly onShowFiles: () => void;
}): ReactNode => {
    const { t } = useLingui();
    // View-ephemeral: the search, the keyboard row, the rows shown, the drag.
    const [query, setQuery] = useState("");
    // -1: no keyboard row yet (nothing highlighted until arrows or hover).
    const [active, setActive] = useState(-1);
    const [shown, setShown] = useState(YOUR_SOURCES_PAGE);
    const [dragging, setDragging] = useState<string | null>(null);
    const listRef = useRef<HTMLDivElement>(null);

    const groups = groupYourSources(sources, query, written, shown);
    const rows = [...groups.matches, ...groups.all];
    const activeIndex = Math.min(active, rows.length - 1);
    // Scroll only the list (never the box) so the keyboard row stays seen.
    const keepInView = (index: number) => {
        const list = listRef.current;
        const el = list?.querySelector(`#${optionId(index)}`);
        if (list == null || el == null) return;
        const box = el.getBoundingClientRect();
        const view = list.getBoundingClientRect();
        if (box.top < view.top) list.scrollTop -= view.top - box.top;
        else if (box.bottom > view.bottom)
            list.scrollTop += box.bottom - view.bottom;
    };
    const add = (s: YourSource) => {
        if (!full) onAdd(s);
    };

    const row = (s: YourSource, i: number): ReactNode => (
        // biome-ignore lint/a11y/useKeyWithClickEvents: an option of the combobox; its keys live on the search input (arrows + Enter), which keeps focus.
        <div
            key={s.id}
            id={optionId(i)}
            role="option"
            // Not a tab stop: the search keeps focus and points here.
            tabIndex={-1}
            aria-selected={false}
            aria-disabled={full || undefined}
            data-active={i === activeIndex || undefined}
            data-dragging={dragging === s.id || undefined}
            className="bilda-your-sources__row"
            title={s.title}
            draggable={!full}
            onDragStart={(event) => {
                event.dataTransfer.setData(
                    RESOURCE_DRAG_MIME,
                    encodeResourceDrag({
                        kind: "source",
                        id: s.id,
                        title: s.title,
                    }),
                );
                event.dataTransfer.effectAllowed = "copy";
                setDragging(s.id);
            }}
            onDragEnd={() => setDragging(null)}
            onClick={() => add(s)}
            onMouseEnter={() => setActive(i)}
        >
            <span className="bilda-your-sources__kind" aria-hidden="true">
                <Icon name={kindIcon(s.kind)} size="sm" />
            </span>
            <span className="bilda-your-sources__text">
                <span className="bilda-your-sources__name">{s.title}</span>
                {s.kind === "image" ? (
                    <span className="bilda-your-sources__meta">{t`Image`}</span>
                ) : null}
            </span>
            <span className="bilda-your-sources__plus" aria-hidden="true">
                <Icon name="plus" size="sm" />
            </span>
        </div>
    );

    return (
        <>
            <div className="bilda-your-sources__search">
                <Icon name="search" size="sm" />
                <Input
                    role="combobox"
                    aria-label={t`Search your sources`}
                    aria-expanded="true"
                    aria-controls={LISTBOX_ID}
                    aria-autocomplete="list"
                    aria-activedescendant={
                        activeIndex >= 0 ? optionId(activeIndex) : undefined
                    }
                    placeholder={t`Search your sources`}
                    maxLength={SOURCE_SEARCH_MAX}
                    value={query}
                    onChange={(e) => {
                        setQuery(e.target.value);
                        setActive(-1);
                        setShown(YOUR_SOURCES_PAGE);
                    }}
                    onKeyDown={(event) => {
                        const action = listKey(
                            event.key,
                            activeIndex,
                            rows.length,
                        );
                        switch (action.type) {
                            case "move":
                                event.preventDefault();
                                setActive(action.index);
                                keepInView(action.index);
                                return;
                            case "toggle": {
                                const s = rows[action.index];
                                if (s === undefined) return;
                                event.preventDefault();
                                add(s);
                                return;
                            }
                            case "none":
                                return;
                            default:
                                exhaustSwitchCase(action);
                        }
                    }}
                />
            </div>
            {inFiles > 0 ? (
                <div className="bilda-your-sources__summary">
                    <Icon name="check" size="xs" />
                    <span className="bilda-your-sources__summary-text">
                        {t`${plural(inFiles, {
                            one: "# added to Files",
                            other: "# added to Files",
                        })}`}
                    </span>
                    <button
                        type="button"
                        className="bilda-your-sources__show"
                        onClick={onShowFiles}
                    >
                        {t`Show`}
                    </button>
                </div>
            ) : null}
            {/* Everything is in Files and nothing is searched: the summary
                line says it all, no "No sources match". */}
            {rows.length === 0 && (query !== "" || sources.length > 0) ? (
                <Text
                    as="p"
                    size="sm"
                    tone="dim"
                    className="bilda-your-sources__line"
                >
                    {t`No sources match`}
                </Text>
            ) : null}
            <div
                ref={listRef}
                id={LISTBOX_ID}
                role="listbox"
                aria-label={t`Your sources`}
                aria-multiselectable="true"
                className="bilda-your-sources__list"
            >
                {groups.matches.length > 0 ? (
                    // biome-ignore lint/a11y/useSemanticElements: a group of options inside a listbox; a fieldset may not sit there.
                    <div
                        role="group"
                        aria-labelledby="your-sources-matches"
                        className="bilda-your-sources__group"
                    >
                        <span
                            id="your-sources-matches"
                            className="bilda-your-sources__label"
                        >
                            {t`Matches what you wrote`}
                        </span>
                        {groups.matches.map((s, i) => row(s, i))}
                    </div>
                ) : null}
                {groups.all.length > 0 ? (
                    // biome-ignore lint/a11y/useSemanticElements: a group of options inside a listbox; a fieldset may not sit there.
                    <div
                        role="group"
                        aria-labelledby="your-sources-rest"
                        className="bilda-your-sources__group"
                    >
                        <span
                            id="your-sources-rest"
                            className="bilda-your-sources__label"
                        >
                            {t`Not added`}
                        </span>
                        {groups.all.map((s, i) =>
                            row(s, groups.matches.length + i),
                        )}
                    </div>
                ) : null}
            </div>
            {groups.more > 0 ? (
                <Button
                    variant="ghost"
                    onClick={() => setShown(shown + YOUR_SOURCES_PAGE)}
                >
                    {t`Show more`}
                </Button>
            ) : null}
        </>
    );
};
